// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Alert, AppIcon, Spinner, Button, Dropdown, TablePagination, TextInput } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { adminApi } from '../api/adminApi';
import { useAdmin } from '../contexts/AdminContext';
import type { AdvisorWithAdmin } from '../types/admin';

interface LOAWithAdmin {
  id: string;
  profileId: string;
  profileName: string;
  advisorId?: string;
  advisorName?: string;
  status: string;
  policyCount: number;
  signatoryCount: number;
  signedCount: number;
  createdAt: string;
  completedAt?: string;
  sentAt?: string;
}

interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

const AllLOAsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: adminLoading } = useAdmin();

  const [loas, setLoas] = useState<PageResponse<LOAWithAdmin> | null>(null);
  const [advisors, setAdvisors] = useState<AdvisorWithAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staleLOACount, setStaleLOACount] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [loasDataRaw, advisorsData] = await Promise.all([
        adminApi.getAllLOAs(currentPage, pageSize),
        adminApi.listAdvisors({ status: 'active' }, 0, 100),
      ]);

      const loasData = loasDataRaw as PageResponse<LOAWithAdmin>;
      setLoas(loasData);
      setAdvisors(advisorsData.content);

      // Count stale LOAs (pending > 7 days)
      const staleCount = (loasData.content || []).filter((loa) => {
        if (loa.status !== 'PENDING_SIGNATURE') return false;
        const sentDate = new Date(loa.sentAt || loa.createdAt);
        const daysSinceSent = (Date.now() - sentDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceSent > 7;
      }).length;
      setStaleLOACount(staleCount);
    } catch (err) {
      console.error('Failed to load LOAs:', err);
      setError('Failed to load LOAs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadData();
    } else if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [adminLoading, isAdmin, loadData, navigate]);

  // Filter LOAs
  const filteredLOAs = (loas?.content || []).filter(loa => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!loa.profileName?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (advisorFilter !== 'all' && loa.advisorId !== advisorFilter) {
      return false;
    }
    if (statusFilter !== 'all' && loa.status?.toLowerCase() !== statusFilter.toLowerCase()) {
      return false;
    }
    return true;
  });

  // Filter options
  const advisorOptions = [
    { key: 'all', text: 'All Advisors', value: 'all' },
    ...advisors.map(a => ({
      key: a.id,
      text: `${a.firstName} ${a.lastName}`,
      value: String(a.id),
    })),
  ];

  const statusOptions = [
    { key: 'all', text: 'All Statuses', value: 'all' },
    { key: 'draft', text: 'Draft', value: 'draft' },
    { key: 'generating', text: 'Generating', value: 'generating' },
    { key: 'pending_signature', text: 'Pending Signature', value: 'pending_signature' },
    { key: 'completed', text: 'Completed', value: 'completed' },
    { key: 'declined', text: 'Declined', value: 'declined' },
    { key: 'expired', text: 'Expired', value: 'expired' },
    { key: 'voided', text: 'Voided', value: 'voided' },
  ];

  const getStatusColor = (status: string): 'grey' | 'blue' | 'yellow' | 'green' | 'red' | 'orange' => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'grey';
      case 'generating': return 'blue';
      case 'pending_signature': return 'yellow';
      case 'completed': return 'green';
      case 'declined': return 'red';
      case 'expired': return 'orange';
      case 'voided': return 'red';
      default: return 'grey';
    }
  };

  const statusColorMap: Record<string, string> = {
    grey: '#767676',
    blue: '#2185d0',
    yellow: '#fbbd08',
    green: '#21ba45',
    red: '#db2828',
    orange: '#f2711c',
  };

  if (adminLoading || loading) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Spinner />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert variant="error" header="Access Denied">
          <p>You do not have permission to access this page.</p>
        </Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ marginTop: '2rem' }}>
        <Alert variant="error" header="Error">
          <p>{error}</p>
          <Button variant="filled" onClick={loadData}>Retry</Button>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ padding: '2em 3em' }}>
      {/* Breadcrumb */}
      <nav style={{ marginBottom: '1em', fontSize: '1.1em', color: '#666' }}>
        <button
          type="button"
          style={{ color: '#4169E1', cursor: 'pointer', textDecoration: 'none', background: 'transparent', border: 0, font: 'inherit', padding: 0 }}
          onClick={() => navigate('/')}
        >
          Dashboard
        </button>
        {' / '}
        <span>Admin</span>
        {' / '}
        <span>All LOAs</span>
      </nav>

      {/* Header */}
      <div style={{ marginBottom: '1em' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
          <AppIcon name="file alternate" />
          All Letters of Authority
          {loas && (
            <span style={{ marginLeft: '0.5em', background: '#2185d0', color: 'white', borderRadius: '50%', padding: '2px 8px', fontSize: '0.75em' }}>
              {loas.totalElements}
            </span>
          )}
        </h1>
        <p style={{ color: '#666', margin: '0.25em 0 0 0' }}>All LOAs across the brokerage</p>
      </div>

      {/* Stale LOA Alert */}
      {staleLOACount > 0 && (
        <Alert variant="warning" header={`${staleLOACount} LOAs pending for more than 7 days`}>
          <p>
            <Button variant="outlined" size="sm" onClick={() => setStatusFilter('pending_signature')}>
              View All
            </Button>
          </p>
        </Alert>
      )}

      {/* Filters */}
      <div style={{ padding: '1rem', background: '#f9f9f9', borderRadius: '4px', marginBottom: '1em' }}>
        <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap', alignItems: 'center' }}>
          <TextInput
            iconLeft={<AppIcon name="search" size={16} color="#333333" />}
            placeholder="Search by relationship name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Dropdown
            placeholder="Status"
            options={statusOptions.map(o => ({ value: o.value, text: o.text }))}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
          />
          <Dropdown
            placeholder="Advisor"
            options={advisorOptions.map(o => ({ value: o.value, text: o.text }))}
            value={advisorFilter}
            onChange={(value) => setAdvisorFilter(value)}
          />
          <Button variant="outlined" size="sm" onClick={loadData}>
            <AppIcon name="refresh" />
          </Button>
        </div>
      </div>

      {/* LOAs Table */}
      {filteredLOAs.length === 0 ? (
        <Alert variant="info" header="No LOAs Found">
          <p>
            {searchQuery || advisorFilter !== 'all' || statusFilter !== 'all'
              ? 'No LOAs match the current filters.'
              : 'No LOAs exist in the brokerage.'}
          </p>
        </Alert>
      ) : (
        <div style={{ background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0', padding: '1rem' }}>
          <table className="fin-table">
            <thead>
              <tr>
                <th>Relationship</th>
                <th>Advisor</th>
                <th>Policies</th>
                <th>Status</th>
                <th>Signatory Progress</th>
                <th>Created</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLOAs.map((loa) => (
                <tr key={loa.id}>
                  <td>
                    <Link
                      to={`/relationships/${loa.profileId}`}
                      style={{ color: '#4169E1', textDecoration: 'none', fontWeight: 'bold' }}
                    >
                      {loa.profileName || 'Unnamed Relationship'}
                    </Link>
                  </td>
                  <td>
                    {loa.advisorName || <em style={{ color: '#999' }}>Unassigned</em>}
                  </td>
                  <td>{loa.policyCount || 0}</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '3px',
                        fontSize: '0.85em',
                        background: statusColorMap[getStatusColor(loa.status)] || '#767676',
                        color: 'white',
                      }}
                    >
                      {loa.status?.replaceAll('_', ' ') || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    {loa.signatoryCount > 0 ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                        <div style={{ flex: 1, background: '#e0e0e0', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${Math.round((loa.signedCount / loa.signatoryCount) * 100)}%`,
                              height: '100%',
                              background: loa.signedCount === loa.signatoryCount ? '#21ba45' : '#2185d0',
                              borderRadius: '4px',
                            }}
                          />
                        </div>
                        <span style={{ fontSize: '0.9em', whiteSpace: 'nowrap' }}>
                          {loa.signedCount}/{loa.signatoryCount}
                        </span>
                      </div>
                    ) : (
                      <em style={{ color: '#999' }}>-</em>
                    )}
                  </td>
                  <td>{formatDate(loa.createdAt)}</td>
                  <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'center' }}>
                      <Button variant="filled" size="sm" onClick={() => navigate(`/relationships/${loa.profileId}`)}>
                        View
                      </Button>
                      {loa.status === 'COMPLETED' && (
                        <Button variant="outlined" size="sm">
                          <AppIcon name="download" /> Download
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {loas && loas.totalPages > 1 && (
        <TablePagination
          config={{
            currentPage: currentPage + 1,
            totalPages: loas.totalPages,
            itemsPerPage: pageSize,
            itemsPerPageOptions: [10, 20, 50],
            onPageChange: (next) => setCurrentPage(next - 1),
            onItemsPerPageChange: (size) => {
              setPageSize(size);
              setCurrentPage(0);
            },
          }}
        />
      )}
    </div>
  );
};

export default AllLOAsPage;
